import React, { useState, useEffect } from 'react';
import DataService from '../../services/data.service';
import { Alert, Spinner } from 'react-bootstrap';

const SquareSettings = () => {
  const [settings, setSettings] = useState({
    environment: 'sandbox',
    sandbox_access_token: '',
    sandbox_location_id: '',
    production_access_token: '',
    production_location_id: '',
    is_active: 'sandbox'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await DataService.getSquareSettings();
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      setMessage({ type: 'danger', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await DataService.saveSquareSettings(settings);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Square settings saved successfully!' });
        loadSettings(); // Reload to get masked tokens
      } else {
        setMessage({ type: 'danger', text: response.data.message || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error saving settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <h2 className="mb-4">Square API Settings</h2>

          {message.text && (
            <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
              {message.text}
            </Alert>
          )}

          <div className="card shadow-sm">
            <div className="card-body">
              <form onSubmit={handleSubmit}>

                {/* Active Environment Toggle */}
                <div className="mb-4 p-3 bg-light rounded">
                  <label className="form-label fw-bold">Active Environment</label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="is_active"
                        id="activeSandbox"
                        value="sandbox"
                        checked={settings.is_active === 'sandbox'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="activeSandbox">
                        Sandbox (Testing)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="is_active"
                        id="activeProduction"
                        value="production"
                        checked={settings.is_active === 'production'}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="activeProduction">
                        Production (Live)
                      </label>
                    </div>
                  </div>
                  <small className="text-muted">
                    Currently using: <strong>{settings.is_active === 'sandbox' ? 'Sandbox (Testing)' : 'Production (Live)'}</strong>
                  </small>
                </div>

                {/* Sandbox Credentials */}
                <div className="mb-4">
                  <h5 className="mb-3">Sandbox Credentials</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="sandbox_access_token" className="form-label">
                        Sandbox Access Token
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="sandbox_access_token"
                        name="sandbox_access_token"
                        value={settings.sandbox_access_token}
                        onChange={handleChange}
                        placeholder="Enter sandbox access token"
                      />
                      <small className="text-muted">
                        Leave unchanged to keep existing token
                      </small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="sandbox_location_id" className="form-label">
                        Sandbox Location ID
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="sandbox_location_id"
                        name="sandbox_location_id"
                        value={settings.sandbox_location_id}
                        onChange={handleChange}
                        placeholder="Enter sandbox location ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Production Credentials */}
                <div className="mb-4">
                  <h5 className="mb-3">Production Credentials</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="production_access_token" className="form-label">
                        Production Access Token
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="production_access_token"
                        name="production_access_token"
                        value={settings.production_access_token}
                        onChange={handleChange}
                        placeholder="Enter production access token"
                      />
                      <small className="text-muted">
                        Leave unchanged to keep existing token
                      </small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="production_location_id" className="form-label">
                        Production Location ID
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="production_location_id"
                        name="production_location_id"
                        value={settings.production_location_id}
                        onChange={handleChange}
                        placeholder="Enter production location ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Security Notice */}
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Security Note:</strong> Access tokens are masked when displayed for security.
                  Only update the token field if you need to change it.
                </div>

                {/* Submit Button */}
                <div className="text-end">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Help Section */}
          <div className="card mt-4 shadow-sm">
            <div className="card-body">
              <h5 className="card-title">
                <i className="fas fa-question-circle me-2"></i>
                How to Get Square API Credentials
              </h5>
              <ol className="mb-0">
                <li className="mb-2">
                  Go to <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer">
                    Square Developer Dashboard
                  </a>
                </li>
                <li className="mb-2">Select your application</li>
                <li className="mb-2">
                  For <strong>Sandbox</strong>: Go to "Sandbox" tab and copy the Access Token
                </li>
                <li className="mb-2">
                  For <strong>Production</strong>: Go to "Production" tab and copy the Access Token
                </li>
                <li className="mb-2">
                  Location IDs can be found in your Square Dashboard under "Locations"
                </li>
                <li>
                  Always test with Sandbox before switching to Production
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SquareSettings;
